import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { type Editor } from "@tiptap/react";
import {
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  offset,
  flip,
  shift,
  autoUpdate,
} from "@floating-ui/react";
import { cn } from "@/utils/cn";
import humanizeKind from "./humanizeKind";

interface InsertSectionDropdownProps {
  editor: Editor;
  kinds: string[];
}

const InsertSectionDropdown: React.FC<InsertSectionDropdownProps> = ({
  editor,
  kinds,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const handleSelect = (
    kind: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    editor.chain().focus().insertSection({ kind }).run();
    setIsOpen(false);
  };

  if (kinds.length === 0) return null;

  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        {...getReferenceProps()}
        onMouseDown={(event) => event.preventDefault()}
        className={cn(
          "bg-cream-400 dark:bg-cream-800 flex flex-row items-center gap-1 rounded-lg px-3 py-1 text-sm font-medium shadow hover:cursor-pointer hover:shadow-lg lg:text-base",
          isOpen && "bg-cream-800 text-cream-100 dark:bg-cream-700",
        )}
      >
        Insert Section
        <ChevronDown size={16} strokeWidth={1.5} />
      </button>

      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className="ring-cream-200 dark:bg-cream-900 dark:ring-cream-700 z-50 min-w-[160px] overflow-hidden rounded-lg bg-white shadow-lg ring-1"
        >
          <div className="py-1">
            {kinds.map((kind) => (
              <button
                key={kind}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => handleSelect(kind, event)}
                className="text-cream-900 hover:bg-cream-100 dark:text-cream-100 dark:hover:bg-cream-800 block w-full px-4 py-2 text-left text-sm"
              >
                {humanizeKind(kind)}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default InsertSectionDropdown;
